/**
 * Medya istemcisi — yükleme ve YETKİLİ görüntüleme.
 *
 * =============================================================================
 * NEDEN DÜZ BİR <img src> YETMİYOR
 * -----------------------------------------------------------------------------
 * Dosyalar private bir kovada duruyor ve sunucu paylaşılabilir adres üretmiyor;
 * her okuma Authorization başlığı taşımak zorunda. Tarayıcı `<img src>` isteğine
 * başlık ekleyemez. Bu yüzden içerik `fetch` ile alınır, `blob:` adresine
 * çevrilir ve `<img>` ona bakar.
 *
 * `blob:` adresi YALNIZCA bu sekmede ve yalnızca serbest bırakılana kadar
 * geçerlidir; kopyalanıp başkasına gönderilemez. Yani ekranda görünen şey bir
 * bağlantı değil, o oturuma ait geçici bir görüntüdür.
 *
 * BELLEK: her createObjectURL, revoke edilene kadar blob'u bellekte tutar.
 * `useMedyaGoruntusu` bunu bileşen sökülünce serbest bırakır — kişi listesinde
 * gezinirken bırakılmasa, panel açık kaldıkça bellek şişerdi.
 */
import { useEffect, useState } from "react";
import { ApiHatasi, adres, tokenOku } from "./api.js";

export type MedyaTuru = "kisi_foto" | "yukleme_dekont" | "okul_logo" | "mal_kabul_fis";

export interface MedyaYuklemeSonucu {
  medya_id: string;
  tur: MedyaTuru;
  icerik_tipi: string;
  boyut: number;
  dosya_adi: string | null;
}

/**
 * Dosyayı yükler ve kimliğini döner. Dosya HENÜZ hiçbir kayda BAĞLI DEĞİLDİR;
 * bağ, dönen kimliğin kişi/bildirim formuna konup gönderilmesiyle kurulur.
 */
export async function medyaYukle(
  dosya: File,
  tur: MedyaTuru,
): Promise<MedyaYuklemeSonucu> {
  const govde = new FormData();
  govde.append("dosya", dosya, dosya.name);

  const token = tokenOku();
  const yanit = await fetch(adres("/medya", { tur }), {
    method: "POST",
    // Content-Type BİLEREK verilmez: FormData'nın multipart sınırını
    // tarayıcının kendisi üretmelidir. Elle yazılan bir Content-Type sınırı
    // bozar ve sunucu "dosya bulunamadı" der.
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: govde,
  });

  if (!yanit.ok) {
    let mesaj = `Yükleme başarısız (HTTP ${yanit.status}).`;
    let kod = "SUNUCU_HATASI";
    try {
      const hata = (await yanit.json()) as { hata?: string; mesaj?: string };
      mesaj = hata.mesaj ?? mesaj;
      kod = hata.hata ?? kod;
    } catch {
      /* gövdesiz hata */
    }
    throw new ApiHatasi(yanit.status, kod, mesaj);
  }
  return (await yanit.json()) as MedyaYuklemeSonucu;
}

/** İçeriği indirir ve bu sekmeye ait geçici bir `blob:` adresi üretir. */
export async function medyaObjeUrl(medyaId: string): Promise<string> {
  const token = tokenOku();
  const yanit = await fetch(adres(`/medya/${medyaId}`), {
    headers: {
      Accept: "*/*",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!yanit.ok) {
    throw new ApiHatasi(yanit.status, "BULUNAMADI", "Dosya görüntülenemedi.");
  }
  return URL.createObjectURL(await yanit.blob());
}

/**
 * Görüntülenebilir bir adres döner.
 *
 * `eskiAdres` 0015 öncesi kayıtların serbest metin URL'sidir: yeni bir medya
 * bağı yoksa o gösterilir, böylece geçmiş kayıtların fotoğrafı kaybolmaz.
 */
export function useMedyaGoruntusu(
  medyaId: string | null | undefined,
  eskiAdres?: string | null,
): { url: string | null; yukleniyor: boolean; hata: string | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  useEffect(() => {
    if (!medyaId) {
      setUrl(eskiAdres ?? null);
      setHata(null);
      return;
    }

    let iptal = false;
    let uretilen: string | null = null;
    setYukleniyor(true);
    setHata(null);

    medyaObjeUrl(medyaId)
      .then((yeni) => {
        uretilen = yeni;
        // Bileşen sökülmüşse adresi HEMEN serbest bırak; state'e yazmak da
        // React uyarısı üretirdi, blob da bellekte asılı kalırdı.
        if (iptal) {
          URL.revokeObjectURL(yeni);
          return;
        }
        setUrl(yeni);
      })
      .catch((e: unknown) => {
        if (!iptal)
          setHata(e instanceof Error ? e.message : "Görüntülenemedi.");
      })
      .finally(() => {
        if (!iptal) setYukleniyor(false);
      });

    return () => {
      iptal = true;
      if (uretilen) URL.revokeObjectURL(uretilen);
    };
  }, [medyaId, eskiAdres]);

  return { url, yukleniyor, hata };
}
